<template>
  <div class="pagination" v-show="totalCount || showIfEmpty">
    <div
      :class="{ 'pagination-item-container--no-overflow': centered }"
      class="pagination-item-container flex-shrink-0"
    >
      <PerPageSelect
        v-show="showOptions"
        v-model="selectedPerPage"
        :items="perPageOptions"
        :disabled="disabled"
        :class="{ pagination__dropdown: centered }"
        @change="onPerPageChange"
      ></PerPageSelect>
      <ItemsCounter v-if="selectedCount" :count="selectedCount" />
    </div>
    <slot />
    <div class="pagination-item-container flex-grow-1">
      <div class="pagination-nav" v-if="pages > 1">
        <v-btn
          icon
          @click="currentPage--"
          :disabled="currentPage <= 1 || disabled"
          class="pagination-nav-btn prev-btn"
        >
          <v-icon size="24">mdi-chevron-left</v-icon>
        </v-btn>
        <v-text-field
          :value="currentPage"
          @input="onPageChange"
          outlined
          rounded
          :hide-details="true"
          :disabled="disabled"
          class="page-input"
        ></v-text-field>
        <div class="pages">
          <span class="pages-of">
            {{ $t("message.common.ofPages") }}
          </span>
          <span class="pages-count">{{ pages }}</span>
        </div>
        <v-btn
          icon
          @click="currentPage++"
          :disabled="currentPage >= pages || disabled"
          class="pagination-nav-btn next-btn"
        >
          <v-icon size="24">mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>
    <div
      v-if="centered"
      class="pagination-item-container flex-shrink-0 flex-grow-1"
    ></div>
  </div>
</template>

<script>
import PerPageSelect from "@/components/List/PerPageSelect";
import ItemsCounter from "@/components/List/ItemsCounter";
import cuid from "cuid";
import { sortBy } from "lodash";
import { debounce, pluralCount } from "@/helpers/helpers";

export default {
  name: "Pagination",
  inheritAttrs: false,
  components: {
    PerPageSelect,
    ItemsCounter
  },
  props: {
    page: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    },
    selectedCount: {
      type: Number
    },
    totalCount: {
      type: Number,
      required: true
    },
    options: {
      type: Array,
      default: () => [5, 10, 15]
    },
    disabled: {
      type: Boolean
    },
    showOptions: {
      type: Boolean,
      default: () => true
    },
    showIfEmpty: {
      type: Boolean,
      default: false
    },
    centered: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    perPage(oldValue, newValue) {
      if (oldValue !== newValue) {
        this.selectPerPage();
      }
    }
  },
  data() {
    return {
      selectedPerPage: { name: 15, value: 15, id: cuid() },
      perPageOptions: []
    };
  },
  computed: {
    pages() {
      return Math.ceil(this.totalCount / this.selectedPerPage.value);
    },
    currentPage: {
      get: function() {
        return this.page;
      },
      set: function(value) {
        let page = 1;

        if (value >= 1) {
          if (value > this.pages) {
            page = +this.pages;
          } else {
            page = +value;
          }
        }

        if (page !== this.page) {
          this.$emit("change", {
            page,
            perPage: this.selectedPerPage.value
          });
        }
      }
    }
  },
  mounted() {
    this.initOptions();
    this.selectPerPage();
  },
  methods: {
    initOptions() {
      this.perPageOptions = this.options.map(size => ({
        name: this.getOptionLabel(size),
        value: size,
        id: cuid()
      }));

      if (!this.options.includes(this.perPage)) {
        this.perPageOptions.push({
          name: this.getOptionLabel(this.perPage),
          value: this.perPage,
          id: cuid()
        });

        this.perPageOptions = sortBy(this.perPageOptions, "value");
      }
    },
    selectPerPage() {
      this.selectedPerPage =
        this.perPageOptions.find(({ value }) => value === this.perPage) ||
        this.perPageOptions[0];
    },
    onPerPageChange() {
      this.$emit("change", {
        page: 1,
        perPage: this.selectedPerPage.value
      });
    },
    onPageChange: debounce(function(value) {
      this.currentPage = value;
    }, 500),
    getOptionLabel(value) {
      return this.$t("message.common.show", [value]);
    }
  }
};
</script>

<style lang="scss" scoped>
.pagination {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  min-height: 22px;

  &__dropdown {
    ::v-deep .v-input {
      border-radius: 30px;
      box-shadow: 0px 0px 30px rgba(54, 60, 86, 0.07);
    }
  }

  .pagination-item-container {
    display: flex;
    align-items: center;
    overflow: hidden;
    min-width: 0;

    &--no-overflow {
      overflow: visible;
    }
  }

  .pagination-nav {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: auto;
    user-select: none;
    justify-content: flex-end;

    .pages {
      font-weight: normal;
      @include font-size(14px);
      line-height: 17px;
      color: $text;
    }

    .prev-btn {
      margin-right: $spacing * 2;
    }

    .next-btn {
      margin-left: $spacing * 2;
    }

    .pages-of {
      margin: 0 $spacing * 1.5;
    }
  }
}
</style>

<style lang="scss">
.v-input.page-input {
  max-width: 64px;

  input {
    text-align: center;
    font-weight: normal;
  }

  &.v-text-field--outlined fieldset {
    border: 1px solid rgba($primary, 0.5);
  }
}
</style>
